import React, { useEffect, useRef } from "react";
import type { AuthState, RefreshMethod } from "../types";
import { tokenManager } from "../utils/token-manager";
import { debugError, debugLog } from "../utils";
import { createAuthError } from "../utils";


/**
 * Initializes the authentication state by checking for an existing access/refresh token.
 * @param setState
 * @param refresh - The refresh method to use for token refresh
 */
export function useAuthInitialization(
  setState: React.Dispatch<React.SetStateAction<AuthState>>,
  refresh: RefreshMethod,
) {
  const initializationRef = useRef<boolean>(false);

  useEffect(() => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    const initializeAuth = async () => {
      debugLog("Starting auth initialization");
      try {
        const accessToken = tokenManager.getAccessToken();

        if (accessToken) {
          debugLog("Found access token, attempting refresh");
          try {
            const response = await refresh();

            debugLog("Auth initialization successful", { userId: response.user.id });
            tokenManager.setAccessToken(response.accessToken);

            setState(prev => ({
              ...prev,
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              isAuthenticating: false,
              error: null,
            }));
          } catch (error) {
            debugError("Auth initialization failed - refresh error", error);
            // If refresh fails, clear tokens and set as unauthenticated
            tokenManager.clearTokens();
            setState(prev => ({
              ...prev,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              isAuthenticating: false,
              error: null,
            }));
          }
        } else {
          debugLog("No access token found, user not authenticated");
          setState(prev => ({
            ...prev,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isAuthenticating: false,
            error: null,
          }));
        }
      } catch (error) {
        debugError("Auth initialization failed", error);
        setState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isAuthenticating: false,
          error: createAuthError(error, "Authentication initialization failed"),
        }));
      }
    };

    initializeAuth()
      .catch(console.error);
  }, [setState, refresh]);
}
